import { useState } from 'react'
import { formatShoppingItem } from '../utils/format'
import { shoppingListText } from '../utils/shopping'
import type { ShoppingItem } from '../types'

type ShoppingListProps = {
  items: ShoppingItem[]
  onChange: (items: ShoppingItem[]) => void
  onBack: () => void
}

const copyWithLegacyApi = (text: string): boolean => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  try {
    textarea.select()
    return document.execCommand('copy')
  } finally {
    textarea.remove()
  }
}

export function ShoppingList({ items, onChange, onBack }: ShoppingListProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle')
  const pending = items.filter((item) => !item.checked)
  const checked = items.filter((item) => item.checked)

  const toggle = (id: string) =>
    onChange(items.map((item) => (item.id === id ? { ...item, checked: !item.checked } : item)))
  const remove = (id: string) => onChange(items.filter((item) => item.id !== id))
  const clearChecked = () => onChange(pending)

  const copyList = async () => {
    const text = shoppingListText(items)
    if (!text) return

    try {
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(text)
        } catch {
          if (!copyWithLegacyApi(text)) throw new Error('La copie du presse-papier a échoué')
        }
      } else if (!copyWithLegacyApi(text)) {
        throw new Error('La copie du presse-papier a échoué')
      }
      setCopyState('copied')
    } catch {
      setCopyState('error')
    }
    window.setTimeout(() => setCopyState('idle'), 2400)
  }

  return (
    <main className="page-main shopping-page">
      <button className="back-link" onClick={onBack} type="button">
        <span aria-hidden="true">←</span> Retour aux recettes
      </button>
      <div className="page-intro compact-intro">
        <h1>Liste de courses</h1>
      </div>

      {/* R4: with no items, the empty state below already says so — showing a disabled
          "Copier" button and "0 article à prendre" on top of it says it a third time. */}
      {items.length > 0 && (
        <div className="shopping-actions">
          <span>
            {pending.length} article{pending.length > 1 ? 's' : ''} à prendre
          </span>
          <div className="button-row">
            <button aria-live="polite" className="button secondary" onClick={copyList} type="button">
              {copyState === 'copied'
                ? 'Liste copiée'
                : copyState === 'error'
                  ? 'Copie impossible'
                  : 'Copier la liste de courses'}
            </button>
            {checked.length > 0 && (
              <button className="text-button" onClick={clearChecked} type="button">
                Effacer les cochés
              </button>
            )}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <section className="empty-state">
          <span className="empty-icon" aria-hidden="true">
            ✦
          </span>
          <h2>La liste est vide</h2>
          <p>Ouvre une recette et ajoute ses ingrédients au fur et à mesure de tes préparatifs.</p>
          <button className="button" onClick={onBack} type="button">
            Parcourir les recettes
          </button>
        </section>
      ) : (
        <section className="shopping-list" aria-label="Liste de courses">
          <div className="shopping-items">
            {pending.map((item, index) => (
              <ShoppingRow
                key={`${item.key}-${item.id}-${index}`}
                item={item}
                onToggle={toggle}
                onRemove={remove}
              />
            ))}
          </div>
          {checked.length > 0 && (
            <div className="shopping-items checked-items">
              <p className="list-label">Déjà pris</p>
              {checked.map((item, index) => (
                <ShoppingRow
                  key={`${item.key}-${item.id}-${index}`}
                  item={item}
                  onToggle={toggle}
                  onRemove={remove}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  )
}

function ShoppingRow({
  item,
  onToggle,
  onRemove,
}: {
  item: ShoppingItem
  onToggle: (id: string) => void
  onRemove: (id: string) => void
}) {
  return (
    <div className={`shopping-row${item.checked ? ' is-checked' : ''}`}>
      <label>
        <input checked={item.checked} onChange={() => onToggle(item.id)} type="checkbox" />
        <span className="fake-check" aria-hidden="true">
          ✓
        </span>
        <span>{formatShoppingItem(item)}</span>
      </label>
      <button
        aria-label={`Retirer ${item.label}`}
        className="remove-button"
        onClick={() => onRemove(item.id)}
        type="button"
      >
        ×
      </button>
    </div>
  )
}
